// Mock data for OmniCore X Quantum website

export const services = [
  {
    id: 1,
    title: "AI Growth System",
    shortDesc: "Complete system combining ads, content, website & strategy to generate leads consistently.",
    icon: "brain-circuit",
    image: "https://images.unsplash.com/photo-1694903089438-bf28d4697d9a",
    fullDescription: "Our AI Growth System is a comprehensive solution that integrates advertising, content creation, website optimization, and strategic planning to deliver consistent lead generation results.",
    benefits: [
      "Automated lead generation pipeline",
      "AI-powered campaign optimization",
      "Integrated multi-channel approach",
      "Real-time performance tracking",
      "Scalable growth framework"
    ]
  },
  {
    id: 2,
    title: "Social Media & Google Business Profile",
    shortDesc: "Build trust, visibility, and local presence.",
    icon: "share-2",
    image: "https://images.unsplash.com/photo-1696041756040-c910a971f222",
    fullDescription: "Establish a powerful online presence through strategic social media management and optimized Google Business Profile to attract local customers.",
    benefits: [
      "Enhanced local visibility",
      "Consistent brand presence",
      "Engagement-focused content",
      "Review management",
      "Community building"
    ]
  },
  {
    id: 3,
    title: "Google Ads & Meta Ads",
    shortDesc: "High-converting campaigns focused on leads and ROI.",
    icon: "target",
    image: "https://images.unsplash.com/photo-1677442135703-1787eea5ce01",
    fullDescription: "Data-driven advertising campaigns on Google and Meta platforms designed to maximize conversions and deliver measurable ROI.",
    benefits: [
      "ROI-focused campaigns",
      "Advanced audience targeting",
      "A/B testing optimization",
      "Conversion tracking",
      "Budget efficiency"
    ]
  },
  {
    id: 4,
    title: "Website Design & Management",
    shortDesc: "Premium websites designed to convert visitors into inquiries.",
    icon: "monitor",
    image: "https://images.unsplash.com/photo-1523961131990-5ea7c61b2107",
    fullDescription: "Create stunning, high-performance websites that not only look premium but are engineered to convert visitors into customers.",
    benefits: [
      "Conversion-optimized design",
      "Mobile-responsive layouts",
      "Fast loading speed",
      "SEO-friendly structure",
      "Ongoing maintenance"
    ]
  }
];

export const whyChooseUs = [
  {
    id: 1,
    title: "Growth-Focused Strategy",
    description: "Every decision is made with your growth in mind",
    icon: "trending-up"
  },
  {
    id: 2,
    title: "Lead Generation System",
    description: "Complete systems that generate consistent leads",
    icon: "users"
  },
  {
    id: 3,
    title: "Premium Branding",
    description: "Stand out with professional, modern branding",
    icon: "award"
  },
  {
    id: 4,
    title: "AI-Powered Execution",
    description: "Leverage cutting-edge AI for better results",
    icon: "zap"
  }
];

export const processSteps = [
  {
    id: 1,
    title: "Understanding Business",
    description: "Deep dive into your business goals, target audience, and competitive landscape",
    icon: "search"
  },
  {
    id: 2,
    title: "Strategy & Positioning",
    description: "Create a customized strategy aligned with your objectives and market position",
    icon: "compass"
  },
  {
    id: 3,
    title: "Execution",
    description: "Implement ads, content, and website with precision and expertise",
    icon: "rocket"
  },
  {
    id: 4,
    title: "Optimization & Growth",
    description: "Continuously optimize and scale for maximum performance",
    icon: "line-chart"
  }
];

export const results = [
  {
    id: 1,
    title: "Increase Visibility",
    description: "Be found by customers actively searching for your services",
    icon: "eye"
  },
  {
    id: 2,
    title: "Improve Trust",
    description: "Build credibility and authority in your market",
    icon: "shield-check"
  },
  {
    id: 3,
    title: "Generate More Inquiries",
    description: "Convert visitors into qualified leads consistently",
    icon: "phone-call"
  },
  {
    id: 4,
    title: "Better Patient Flow",
    description: "Attract and retain more patients for healthcare providers",
    icon: "activity"
  }
];

export const blogPosts = [
  {
    id: 1,
    title: "The Future of AI in Digital Marketing",
    excerpt: "Discover how artificial intelligence is revolutionizing the way businesses approach digital marketing in 2025.",
    date: "2025-01-15",
    author: "OmniCore Team",
    image: "https://images.unsplash.com/photo-1677442135703-1787eea5ce01",
    category: "AI & Technology",
    content: `Artificial Intelligence is transforming the digital marketing landscape in unprecedented ways. From predictive analytics to personalized customer experiences, AI is enabling businesses to work smarter, not harder.

## Key AI Applications in Digital Marketing

### 1. Predictive Analytics
AI algorithms can analyze vast amounts of data to predict customer behavior, helping businesses make informed decisions about their marketing strategies.

### 2. Personalization at Scale
Machine learning enables hyper-personalized content delivery, ensuring each customer receives relevant messages at the right time.

### 3. Chatbots and Customer Service
AI-powered chatbots provide 24/7 customer support, handling inquiries instantly and freeing up human resources for complex tasks.

### 4. Content Creation
AI tools can generate content ideas, write copy, and even create visuals, accelerating the content production process.

## The Future Outlook

As AI technology continues to evolve, we can expect even more sophisticated applications in digital marketing. Businesses that embrace AI early will have a significant competitive advantage in the market.`
  },
  {
    id: 2,
    title: "5 Ways to Improve Your Google Business Profile",
    excerpt: "Maximize your local visibility with these proven strategies for optimizing your Google Business Profile.",
    date: "2025-01-10",
    author: "OmniCore Team",
    image: "https://images.unsplash.com/photo-1696041756040-c910a971f222",
    category: "Local SEO",
    content: `Your Google Business Profile (GBP) is one of the most powerful tools for local businesses. Here's how to optimize it for maximum visibility and customer engagement.

## 1. Complete Every Section

Ensure all information is filled out completely, including business hours, contact details, services, and attributes. Google rewards complete profiles with better rankings.

## 2. Post Regular Updates

Share updates, offers, events, and news through Google Posts. Fresh content signals to Google that your business is active and engaged.

## 3. Collect and Respond to Reviews

Actively encourage satisfied customers to leave reviews. Always respond to reviews, both positive and negative, showing you value customer feedback.

## 4. Add High-Quality Photos

Upload professional photos of your business, products, services, and team. Businesses with photos receive 42% more direction requests and 35% more clicks.

## 5. Use Relevant Categories

Select the most accurate primary category and add relevant secondary categories to help Google understand your business better.

## Conclusion

A well-optimized Google Business Profile can significantly increase your local visibility and drive more customers to your business.`
  },
  {
    id: 3,
    title: "Meta Ads vs Google Ads: Which is Right for You?",
    excerpt: "A comprehensive comparison to help you choose the best advertising platform for your business goals.",
    date: "2025-01-05",
    author: "OmniCore Team",
    image: "https://images.unsplash.com/photo-1694903089438-bf28d4697d9a",
    category: "Paid Advertising",
    content: `Choosing between Meta Ads and Google Ads can be challenging. Both platforms offer unique advantages, and the right choice depends on your business goals and target audience.

## Google Ads: Intent-Based Advertising

### Strengths:
- Captures users with high purchase intent
- Excellent for local businesses
- Wide reach across search and display networks
- Strong B2B performance

### Best For:
- Service-based businesses
- B2B companies
- High-ticket items
- Local businesses

## Meta Ads: Interest-Based Advertising

### Strengths:
- Powerful audience targeting
- Visual storytelling capabilities
- Lower cost per click in many industries
- Excellent for brand awareness

### Best For:
- E-commerce businesses
- B2C companies
- Lifestyle brands
- Visual products

## The Verdict

Most successful businesses use both platforms strategically. Google Ads captures demand, while Meta Ads creates it. Consider your budget, goals, and audience when making your decision.`
  },
  {
    id: 4,
    title: "Social Media Strategies That Actually Drive Results",
    excerpt: "Learn the proven social media tactics that top brands use to engage audiences and generate leads.",
    date: "2025-01-02",
    author: "OmniCore Team",
    image: "https://images.unsplash.com/photo-1523961131990-5ea7c61b2107",
    category: "Social Media",
    content: `Social media marketing is more than posting regularly. Here are the strategies that actually drive business results in 2025.

## 1. Focus on Short-Form Video

Platforms like Instagram Reels, TikTok, and YouTube Shorts are dominating engagement metrics. Invest in creating compelling short-form video content.

## 2. Build Community, Not Just Followers

Engagement matters more than follower count. Create content that encourages conversation and builds a loyal community around your brand.

## 3. Leverage User-Generated Content

Encourage customers to create content featuring your products or services. UGC builds trust and provides authentic social proof.

## 4. Use Social Commerce Features

Take advantage of in-app shopping features on Instagram, Facebook, and TikTok to reduce friction in the customer journey.

## 5. Track Meaningful Metrics

Focus on metrics that align with business goals: conversion rates, engagement rates, and customer acquisition costs rather than vanity metrics.`
  },
  {
    id: 5,
    title: "Content Marketing Trends Dominating 2025",
    excerpt: "Stay ahead of the curve with these emerging content marketing trends that are reshaping the industry.",
    date: "2024-12-28",
    author: "OmniCore Team",
    image: "https://images.unsplash.com/photo-1699062139074-f7b6f776c4de",
    category: "Content Marketing",
    content: `Content marketing continues to evolve. Here are the trends shaping the industry in 2025 and how to leverage them for your business.

## Interactive Content

Quizzes, polls, calculators, and interactive infographics are driving engagement rates through the roof. Users spend more time with interactive content and are more likely to convert.

## AI-Assisted Content Creation

While AI can't replace human creativity, it's becoming an invaluable tool for research, ideation, and first drafts, allowing content creators to focus on strategy and refinement.

## Topic Clusters Over Keywords

Modern SEO strategies focus on topic clusters and semantic relationships rather than individual keywords, providing comprehensive coverage of subjects.

## Video-First Strategy

Video content continues to dominate, with businesses investing in everything from short social clips to long-form YouTube content and webinars.

## Personalization at Scale

Advanced segmentation and personalization tools allow businesses to deliver tailored content experiences to different audience segments automatically.`
  },
  {
    id: 6,
    title: "Web Design Trends for Maximum Conversions",
    excerpt: "Discover the latest web design principles that drive user engagement and boost conversion rates.",
    date: "2024-12-20",
    author: "OmniCore Team",
    image: "https://images.unsplash.com/photo-1523961131990-5ea7c61b2107",
    category: "Web Design",
    content: `Modern web design goes beyond aesthetics. Here's what's driving conversions in 2025.

## Micro-Interactions

Subtle animations and feedback when users interact with elements create a more engaging, intuitive experience.

## Dark Mode Options

Offering dark mode isn't just trendy—it improves user experience and accessibility for many visitors.

## Minimalist Navigation

Simplified, intuitive navigation helps users find what they need quickly, reducing bounce rates and improving conversions.

## Speed Optimization

Page load speed directly impacts conversions. Every second of delay can cost you significant revenue.

## Mobile-First Design

With mobile traffic dominating, designing for mobile first ensures the best experience for the majority of your users.`
  },
  {
    id: 7,
    title: "Analytics That Matter: Tracking Real ROI",
    excerpt: "Learn which metrics actually matter for your business and how to track them effectively.",
    date: "2024-12-15",
    author: "OmniCore Team",
    image: "https://images.unsplash.com/photo-1677442135703-1787eea5ce01",
    category: "Analytics",
    content: `Not all metrics are created equal. Focus on the data that truly impacts your bottom line.

## Customer Acquisition Cost (CAC)

Understanding how much it costs to acquire a customer helps you allocate marketing budget effectively.

## Customer Lifetime Value (CLV)

Knowing the long-term value of customers helps you determine how much you can afford to spend on acquisition.

## Conversion Rate by Channel

Track which marketing channels deliver the best conversion rates to optimize your marketing mix.

## Return on Ad Spend (ROAS)

Measure the revenue generated for every dollar spent on advertising to ensure profitability.

## Attribution Modeling

Use multi-touch attribution to understand the complete customer journey and give credit where it's due.`
  },
  {
    id: 8,
    title: "Building a Growth Strategy That Scales",
    excerpt: "A comprehensive guide to creating sustainable growth strategies for your business.",
    date: "2024-12-10",
    author: "OmniCore Team",
    image: "https://images.unsplash.com/photo-1694903089438-bf28d4697d9a",
    category: "Growth Strategies",
    content: `Sustainable growth requires a strategic, systematic approach. Here's how to build a growth strategy that scales.

## Define Clear Objectives

Start with specific, measurable goals aligned with your business objectives. Vague goals lead to unfocused strategies.

## Understand Your Customer

Deep customer insights drive effective growth strategies. Invest time in understanding pain points, motivations, and behaviors.

## Test and Iterate

Use a test-and-learn approach. Small experiments help you identify what works before scaling.

## Build Systems and Processes

Document what works and create repeatable processes. This allows you to scale without losing effectiveness.

## Leverage Automation

Automate repetitive tasks to free up resources for strategic activities that drive growth.`
  }
];

export const allServices = [
  "GMB Management",
  "Meta & Google Ads",
  "Web Design",
  "Graphic Designing",
  "Video Editing",
  "SEO",
  "WhatsApp Marketing",
  "Hospital Marketing"
];

export const contactInfo = {
  email: "sales@omnicorexquantum.com",
  phone: "8185882299",
  whatsappNumber: "8185882299",
  whatsappMessage: "Hi! I'm interested in learning more about your digital marketing services."
};
